import {
  createFilter,
  getSubOption,
  mapSubcategoryToCategory,
} from 'data/filters';
import { AVAILABILITY_FILTER_OPTIONS } from 'data/subOptions/availabilitySubOptions';
import addDays from 'date-fns/addDays';
import cloneDeep from 'lodash.clonedeep';
import isEmpty from 'lodash.isempty';
import uniqWith from 'lodash.uniqwith';
import { Filter } from 'model/Filters';
import formatDate from 'utils/formatDate';

type FiltersIndex = { [uid: string]: Filter };

function removeFilter(uid, option = null) {
  return (index: FiltersIndex) => {
    const { [uid]: filter = { subOptions: [] }, ...rest } = index;
    filter.subOptions = filter.subOptions.filter(
      ({ value }) => value !== option,
    );
    return !filter.subOptions.length ? rest : { [uid]: filter, ...rest };
  };
}

function redirects(activeFilters: Filter[]): string | null {
  const tags = activeFilters.find(
    (filter) => filter.uid === 'tags.items[]',
  ) || { subOptions: [] };
  return (tags.subOptions as any[]).reduce((url, { value }) => {
    switch (value) {
      case 'celebration':
        return '/';
    }
    return url;
  }, null);
}

function addMainFilter(uid, value) {
  return (index: FiltersIndex) => {
    const filter = index[uid] || createFilter(uid);
    if (value) {
      filter.value = value;
    }

    return { ...index, [uid]: filter };
  };
}

function addFilter(uid, optionValue = null) {
  return (index: FiltersIndex) => {
    const filter = index[uid] || createFilter(uid);

    if (optionValue) {
      const option = getSubOption(uid, optionValue);
      filter.subOptions = uniqWith(
        [...filter.subOptions, option],
        (a, b) => a.value === b.value,
      );
    }

    return { ...index, [uid]: filter };
  };
}

function removeCategory(categoryValue) {
  return (index: FiltersIndex) => {
    const subcategories = index['subcategory.items[]'];
    const subOptions = subcategories?.subOptions ?? [];
    if (
      !subOptions.some(
        ({ value }) => mapSubcategoryToCategory[value] === categoryValue,
      )
    ) {
      return removeFilter('activityType.activity', categoryValue)(index);
    }
    return index;
  };
}

const mappings = {
  'subcategory.items[]': {
    'food-tours-tastings': [
      removeCategory('activities'),
      addFilter('subcategory.items[]', 'food-tours'),
    ],
    'home-dinners': [
      addFilter('activityType.activity', 'activities'),
      addFilter('subcategory.items[]', 'home-dinners'),
    ],
    workshops: [
      removeCategory('tours'),
      addFilter('subcategory.items[]', 'workshops'),
    ],
    'photo-tours': [
      removeCategory('activities'),
      addFilter('subcategory.items[]', 'photo-tours'),
    ],
    'city-tours': [
      removeCategory('activities'),
      addFilter('subcategory.items[]', 'city-tours'),
    ],
  },
  'tags.items[]': {
    '*': [removeCategory('tours'), removeCategory('activities')],
    'bike-tours': [
      addFilter('activityType.activity', 'tours'),
      addFilter('other.transport_mode.items[]', 'bike'),
    ],
    'boat-tours': [
      addFilter('activityType.activity', 'tours'),
      addFilter('other.transport_mode.items[]', 'boat'),
    ],
    'day-tours': [
      addFilter('activityType.activity', 'tours'),
      addFilter('duration.items[]', 'full-day'),
    ],
    'layover-tours': [addFilter('tags.items[]', 'layover-tours')],
    'half-day-trips': [
      addFilter('activityType.activity', 'tours'),
      addFilter('subcategory.items[]', 'day-trips'),
      addFilter('duration.items[]', 'half-day'),
    ],
    'music-workshops': [
      addFilter('activityType.activity', 'activities'),
      addFilter('subcategory.items[]', 'workshops'),
    ],
    'local-cuisines': [
      addFilter('activityType.activity', 'activities'),
      addFilter('subcategory.items[]', 'home-dinners'),
    ],
    surfing: [
      addFilter('activityType.activity', 'activities'),
      addFilter('subcategory.items[]', 'sports-leisure'),
    ],
    fishing: [
      addFilter('activityType.activity', 'activities'),
      addFilter('subcategory.items[]', 'wildlife-nature'),
    ],
    'skip-the-line': [
      addFilter('activityType.activity', 'tours'),
      addFilter('subcategory.items[]', 'skip-the-line-tours'),
    ],
    'shopping-tours': [
      addFilter('activityType.activity', 'tours'),
      addFilter('subcategory.items[]', 'shopping-tours'),
    ],
    'wildlife-tours': [
      addFilter('activityType.activity', 'activities'),
      addFilter('subcategory.items[]', 'wildlife-nature'),
    ],
    'adventure-travel': [
      addFilter('activityType.activity', 'activities'),
      addFilter('subcategory.items[]', 'wildlife-nature'),
    ],
    highlights: [
      addFilter('activityType.activity', 'tours'),
      addFilter('subcategory.items[]', 'city-tours'),
    ],
    'nature-tours': [
      addFilter('activityType.activity', 'activities'),
      addFilter('subcategory.items[]', 'wildlife-nature'),
    ],
  },
  'family-friendly': [
    addFilter('family-friendly'),
    removeCategory('activities'),
  ],
  'other.availability.items[]': {
    now: [
      removeFilter('other.availability.items[]'),
      addFilter('other.availability.items[]', AVAILABILITY_FILTER_OPTIONS.NOW),
      addMainFilter('date.from', formatDate(new Date(), 'yyyy-MM-dd')),
      removeFilter('date.to'),
    ],
    today: [
      removeFilter('other.availability.items[]'),
      addFilter(
        'other.availability.items[]',
        AVAILABILITY_FILTER_OPTIONS.TODAY,
      ),
      addMainFilter('date.from', formatDate(new Date(), 'yyyy-MM-dd')),
      removeFilter('date.to'),
    ],
    tomorrow: [
      removeFilter('other.availability.items[]'),
      addFilter(
        'other.availability.items[]',
        AVAILABILITY_FILTER_OPTIONS.TOMORROW,
      ),
      addMainFilter(
        'date.from',
        formatDate(addDays(new Date(), 1), 'yyyy-MM-dd'),
      ),
      removeFilter('date.to'),
    ],
  },
};

function runMappings(mapping, index) {
  return mapping.reduce((result, action) => action(result), index);
}

function mapFilter(filter: Filter, index: FiltersIndex): FiltersIndex {
  const filterMapping = mappings[filter.uid];
  if (filterMapping) {
    if (filter?.subOptions?.length) {
      return filter.subOptions.reduce((result, subOption) => {
        let mappedResult = result;
        const optionMapping = filterMapping[subOption.value] || [];
        if (filterMapping['*']) {
          mappedResult = runMappings(filterMapping['*'], mappedResult);
        }
        if (optionMapping.length) {
          mappedResult = removeFilter(
            filter.uid,
            subOption.value,
          )(mappedResult);
          mappedResult = runMappings(optionMapping, mappedResult);
        }
        return mappedResult;
      }, index);
    }
    if (!isEmpty(filter.value)) {
      return runMappings(filterMapping, removeFilter(filter.uid)(index));
    }
  }
  return index;
}

function map(activeFilters: Filter[]): Filter[] {
  let index: FiltersIndex = activeFilters.reduce(
    (result, filter) => ({ ...result, [filter.uid]: cloneDeep(filter) }),
    {},
  );
  index = activeFilters.reduce(
    (index, filter) => mapFilter(filter, index),
    index,
  );
  return Object.values(index);
}

export default { map, redirects };
